import { Modal, Steps } from 'antd';
import { useCallback, useState } from 'react';

import { useConfirm } from '@app/hooks/useConfirm';

import { CONNECT_CHANNEL_CONFIGS } from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/constants';
import { useIntegrationAuthState } from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/hooks';
import {
  AuthStepState,
  ConnectAuthArchetype,
} from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/types';
import { BotTokenAuthContent } from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/ui/BotTokenAuthContent';
import { ProviderAuthContent } from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/ui/ProviderAuthContent';
import { QrSessionAuthContent } from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/ui/QrSessionAuthContent';

import type { FC } from 'react';

import '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/styles/ConnectIntegrationModal.desktop.scss';

export interface ConnectIntegrationModalProps {
  isOpen: boolean;
  /** SocialType канала (telegram-personal / max / telegram / vk / instagram) */
  channelType: string;
  integrationId: number | null;
  activeBranchId: number | null;
  /** OAuthUrl для архетипа «вход у провайдера» (ВКонтакте, Instagram) */
  authUrl?: string | null;
  onCancel(): void;
}

const STEP_ITEMS = [
  { title: 'Параметры' },
  { title: 'Авторизация' },
  { title: 'Готово' },
];

const AUTH_STEP_INDEX = 1;
const DONE_STEP_INDEX = 2;

interface QrAuthorizationActivity {
  identity: string;
  isActive: boolean;
}

/**
 * Единое окно подключения канала (us/10721): шаги «Параметры → Авторизация →
 * Готово», содержимое шага «Авторизация» определяется архетипом канала.
 * Успех приходит сам — поллинг + push, кнопки «Обновить статус» нет.
 * Дизайн: https://claude.ai/code/artifact/36a783f9-d119-4161-a553-57a7a81be878
 */
export const ConnectIntegrationModal: FC<ConnectIntegrationModalProps> = ({
  isOpen,
  channelType,
  integrationId,
  activeBranchId,
  authUrl = null,
  onCancel,
}) => {
  const channel = CONNECT_CHANNEL_CONFIGS[channelType];
  const { show: showConfirm } = useConfirm();

  const stepInfo = useIntegrationAuthState({
    integrationId,
    activeBranchId,
    isActive: isOpen && Boolean(channel),
  });

  const authorizationIdentity = `${channelType}:${activeBranchId}:${integrationId}`;
  const [qrAuthorizationActivity, setQrAuthorizationActivity] =
    useState<QrAuthorizationActivity | null>(null);

  const handleQrAuthorizationActivityChange = useCallback(
    (isAuthorizationActive: boolean) => {
      setQrAuthorizationActivity({
        identity: authorizationIdentity,
        isActive: isAuthorizationActive,
      });
    },
    [authorizationIdentity]
  );

  const isDone = stepInfo.state === AuthStepState.Success;
  const isQrSession = channel?.archetype === ConnectAuthArchetype.QrSession;
  const defaultQrAuthorizationActivity =
    isOpen &&
    isQrSession &&
    ![AuthStepState.Success, AuthStepState.Error].includes(stepInfo.state);
  const isQrAuthorizationActive =
    qrAuthorizationActivity?.identity === authorizationIdentity
      ? qrAuthorizationActivity.isActive
      : defaultQrAuthorizationActivity;

  const handleCancel = useCallback(() => {
    if (!isQrSession || !isQrAuthorizationActive) {
      onCancel();

      return;
    }

    showConfirm({
      title: 'Закрыть подключение?',
      subtitle:
        'Отслеживание авторизации в этом окне остановится. Уже начатое подключение может завершиться в фоне — результат можно проверить, открыв интеграцию снова.',
      confirmText: 'Закрыть',
      cancelText: 'Продолжить',
      onConfirm: onCancel,
    });
  }, [isQrAuthorizationActive, isQrSession, onCancel, showConfirm]);

  if (!channel) {
    return null;
  }

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      title={`Подключение ${channel.title}`}
      className="connect-integration-modal"
      footer=""
      width={720}
    >
      <Steps
        size="small"
        current={isDone ? DONE_STEP_INDEX : AUTH_STEP_INDEX}
        items={STEP_ITEMS}
        className="connect-integration-modal__steps"
      />
      {channel.archetype === ConnectAuthArchetype.QrSession && (
        <QrSessionAuthContent
          channelType={channelType}
          integrationId={integrationId}
          activeBranchId={activeBranchId}
          stepInfo={stepInfo}
          isActive={isOpen}
          onAuthorizationActivityChange={handleQrAuthorizationActivityChange}
        />
      )}
      {channel.archetype === ConnectAuthArchetype.Token && (
        <BotTokenAuthContent
          integrationId={integrationId}
          activeBranchId={activeBranchId}
          stepInfo={stepInfo}
        />
      )}
      {channel.archetype === ConnectAuthArchetype.Provider && (
        <ProviderAuthContent
          channelTitle={channel.title}
          authUrl={authUrl}
          stepInfo={stepInfo}
          isActive={isOpen}
          hasEmbeddedLogin={channel.hasEmbeddedLogin}
        />
      )}
    </Modal>
  );
};
